import { useState, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import useSWR from "swr";

/**
 * Custom hook for making API requests with state management.
 * Can be used for both authenticated and unauthenticated requests.
 * Now supports caching with SWR when using the cached version.
 *
 * @param {string} cacheKey - Optional cache key for SWR caching
 * @param {object} swrOptions - Optional SWR configuration options
 * @returns {{
 *   data: any | null;
 *   loading: boolean;
 *   error: Error | null;
 *   execute: (endpoint: string, options?: RequestInit & {
 *     responseType?: 'json' | 'blob',
 *     timeout?: number,
 *     requireAuth?: boolean
 *   }) => Promise<any>;
 * }} An object containing the API response data, loading state, error state, and the execute function.
 */

export default function useApi(cacheKey = null, swrOptions = {}) {
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	const { getToken } = useAuth();
	const baseURL = process.env.REACT_APP_BACKEND_URL;

	// Core fetch logic - DRY principle
	const performFetch = useCallback(
		async (
			endpoint,
			{
				responseType = "json",
				timeout = 10000,
				method = "GET",
				headers = {},
				body = null,
				requireAuth = true,
				...restOptions
			} = {},
		) => {
			const fetchWithTimeout = (url, options, timeoutDuration) => {
				return Promise.race([
					fetch(url, options),
					new Promise((_, reject) =>
						setTimeout(
							() => reject(new Error("Request timed out")),
							timeoutDuration,
						),
					),
				]);
			};

			// Only get token if authentication is required
			let token = null;
			if (requireAuth) {
				token = await getToken();
				if (!token) {
					throw new Error("Authentication token not available.");
				}
			}

			// Prepare headers based on whether auth is required
			const authHeaders = token
				? { Authorization: `Bearer ${token}` }
				: {};

			const response = await fetchWithTimeout(
				`${baseURL}${endpoint}`,
				{
					method,
					credentials: "include",
					headers: {
						...authHeaders,
						// Only set Content-Type for JSON, let browser handle FormData
						...(responseType === "json" &&
						body &&
						!(body instanceof FormData)
							? { "Content-Type": "application/json" }
							: {}),
						...headers,
					},
					body:
						method !== "GET" && method !== "HEAD" && body
							? body instanceof FormData
								? body // Don't stringify FormData
								: typeof body === "string"
									? body
									: JSON.stringify(body)
							: null,
					...restOptions,
				},
				timeout,
			);

			if (!response.ok) {
				let errorMessage =
					"Er is iets misgegaan, probeer het later opnieuw.";
				let errorData = null;
				try {
					errorData = await response.json();
					errorMessage = errorData.message || errorMessage;
				} catch (jsonError) {
					console.error("Error parsing error JSON:", jsonError);
				}
				await toast.error(errorMessage);
				const fetchError = new Error(errorMessage);
				fetchError.response = response;
				fetchError.data = errorData;
				throw fetchError;
			}

			// Handle successful response based on responseType
			if (responseType === "json") {
				return await response.json();
			} else if (responseType === "blob") {
				return await response.blob();
			} else {
				return await response.text();
			}
		},
		[getToken, baseURL],
	);

	const execute = useCallback(
		async (endpoint, options = {}) => {
			setLoading(true);
			setError(null);
			setData(null);

			try {
				const responseData = await performFetch(endpoint, options);
				setData(responseData);
				setLoading(false);
				return responseData;
			} catch (err) {
				console.error(`Fetch error for ${endpoint}:`, err);
				setError(err);
				setLoading(false);
				return null;
			}
		},
		[performFetch],
	);

	// SWR fetcher that reuses the core fetch logic
	const fetcher = useCallback(
		async (url) => {
			return await performFetch(url, { requireAuth: true });
		},
		[performFetch],
	);

	// Use SWR if cacheKey is provided
	const swrResult = useSWR(cacheKey, fetcher, {
		revalidateOnFocus: false,
		revalidateOnReconnect: true,
		refreshInterval: 30000,
		...swrOptions,
	});

	// Return SWR result if cacheKey is provided, otherwise return manual state
	if (cacheKey) {
		return {
			data: swrResult.data,
			loading: swrResult.isLoading,
			error: swrResult.error,
			execute,
		};
	}

	return { data, loading, error, execute };
}
